<script setup lang="ts">
import { computed } from "vue";

/**
 * 敲敲当日额度：标题栏里模型选择器右侧的剩余百分比。
 *
 * 这条的活儿只有一件 —— 在被拒之前让人看见自己快撞墙了。所以按「够用就好」排版：
 * - 正常态：剩余百分比。「今日」「额度」这些字不进标题栏（那儿还挤着
 *   返回/搜索/删除），全交给 hover 出来的 title；
 * - 用到 80% 起转橙、打满转红：颜色是不占地方的提示通道；
 * - 只有打满时换成一句「额度已用完」—— 此刻用户要的是「AI 不会回了」这个事实，
 *   不是数字。也只有这一次会撑宽，而它本就该被注意到。
 *
 * 额度按 token 扣（真实 token × 该模型的倍率），绝对数不下发也不显示：换一个倍率
 * 不同的模型，同一句话吃掉的 token 就变了，百分比不会看着莫名其妙。
 */
const props = defineProps<{
  percent: number;
  exhausted: boolean;
  /** 下一次日切（本地 04:00）的 ISO 时刻 */
  resetAt: string;
}>();

/** 标题栏上显示剩余（像游戏里的「剩余邀约」），已用多少由 title 说清 */
const remaining = computed(() => Math.max(0, Math.min(100, 100 - props.percent)));
const label = computed(() => (props.exhausted ? "额度已用完" : `剩余 ${remaining.value}%`));

const resetLabel = computed(() => {
  const t = new Date(props.resetAt);
  if (Number.isNaN(t.getTime())) return "";
  // resetAt 是绝对时刻，换成本地时间显示才是用户能对上的那个「04:00」
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  return `${hh}:${mm} 重置`;
});

const detail = computed(() => {
  const head = props.exhausted ? "今日额度已用完" : `今日额度已用 ${props.percent}%`;
  return resetLabel.value ? `${head} · ${resetLabel.value}` : head;
});

/** 用到 80% 起转橙、打满转红：颜色本身就是提示，不用额外文案 */
const level = computed(() => {
  if (props.exhausted) return "is-full";
  if (props.percent >= 80) return "is-warn";
  return "";
});
</script>

<template>
  <div class="ik-quota" :class="level" :title="detail" :aria-label="detail">
    <span class="ik-quota__label">{{ label }}</span>
  </div>
</template>

<style scoped>
.ik-quota {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  min-width: 0;
  min-height: var(--ik-header-control-size, 36px);
  padding: 0 4px;
  user-select: none;
  font-size: 12px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.38);
  /* 有 title 才有明细，用 help 光标提示可以 hover */
  cursor: help;
  transition: color 140ms ease;
}

.ik-quota:hover {
  color: rgba(255, 255, 255, 0.62);
}

.ik-quota.is-warn {
  color: #ffb36b;
}

.ik-quota.is-full {
  color: #ff8a8a;
}

.ik-quota__label {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* 等宽数字 + 固定最小宽右对齐：0% 一路涨到 100% 都不会把右边的按钮推着走 */
  min-width: 5em;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

@media (prefers-reduced-motion: reduce) {
  .ik-quota {
    transition: none;
  }
}
</style>
